"use client";

import { useEffect } from "react";
import Button from "@/shared/ui/components/button";
import ScheduleList from "@/shared/ui/components/schedule-list";
import Modal, { ModalHead } from "@/shared/ui/components/modal";
import { type AppContext } from "@/types";
import Box from "@/shared/ui/components/box";
import Badge from "@/shared/ui/components/badge";
import {
  emptyPaymentOption,
  isUaeBnplPlusProduct,
  termCostLineItemLabelKey,
} from "@/lib/payment-option";
import { useTranslation } from "react-i18next";
import MoneyAmount from "@/shared/ui/components/money-amount";
import { RewardsIcon } from "@/shared/ui/icons/rewards-icon";
import * as events from "@/events";
import {
  emitModalTracking,
  emitPaymentDetailsBottomSheetTracking,
} from "@/shared/tracking/analytics";
import AprDeEmphasisTooltip from "./apr-de-emphasis-tooltip";
import { useShariaAuditOrderDetailsLabels } from "./use-sharia-audit-order-details-labels";
import { isKsaMarket } from "@/lib/checkout-documents";
import {
  getSubventionFeeAfterWaiverAmount,
  getSubventionFeeStrikethroughAmount,
  shouldShowSubventionFeeStrikethrough,
} from "@/lib/subvention";

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: () => void;
  onShowCart?: () => void;
  selectedPayments: number;
  ctx: AppContext;
  handleContinueToPayment: () => Promise<void>;
}

const BNPL_PRODUCT_TYPE = "BNPL" as const;

export function OrderDetailsModal({
  isOpen,
  onClose,
  onContinue,
  selectedPayments,
  ctx,
  handleContinueToPayment,
}: OrderDetailsModalProps) {
  const { t, i18n } = useTranslation();
  const shouldUseArabicSingularRepaymentLabel =
    i18n.language.startsWith("ar") && selectedPayments >= 11;
  const paymentsLabelKey = shouldUseArabicSingularRepaymentLabel
    ? "common.payments_one"
    : "common.payments";

  const paymentOption =
    ctx.session.paymentOptions.find(
      (paymentOption) => paymentOption.tenure === selectedPayments,
    ) ?? emptyPaymentOption;

  const { schedulePreview } = paymentOption;

  const isBNPLPlus =
    paymentOption.productType !== BNPL_PRODUCT_TYPE &&
    paymentOption.productType !== null;
  const countryCode = (ctx.session.order.countryCode ?? "").toUpperCase();
  const isUaeBnplPlus = isUaeBnplPlusProduct(
    ctx.session.order.countryCode,
    paymentOption.productType,
  );
  // Former Statsig gate apr_product_type_cosmos: KSA 100%, UAE 0%.
  const showProductType = isKsaMarket(countryCode);
  const isKsa = showProductType;

  const aprPercent = (paymentOption.annualPercentageRate ?? 0) * 100;
  const showAprDeEmphasis = isKsa && isBNPLPlus && aprPercent > 0;
  const showInlineApr = isBNPLPlus && !isUaeBnplPlus && !showAprDeEmphasis;

  const totalFees = paymentOption.totalCustomerFeeAmount.amount;
  const showSubventionFeeStrikethrough =
    shouldShowSubventionFeeStrikethrough(paymentOption);
  const strikethroughFeeAmount =
    getSubventionFeeStrikethroughAmount(paymentOption);
  const feeAfterWaiverAmount =
    getSubventionFeeAfterWaiverAmount(paymentOption);
  const appliedRewards = paymentOption.appliedRewards;
  const rewardAmount = appliedRewards?.rewardAmount?.amount ?? 0;
  const rewardCurrency =
    appliedRewards?.rewardAmount?.currency ??
    paymentOption.downPaymentAmount.currency;
  const processingFeeAmount = paymentOption.processingFeeAmount.amount;
  const murabahaProfit = paymentOption.termCostAmount.amount;
  const { effectiveAprLabel, productTypeLabel } =
    useShariaAuditOrderDetailsLabels({
      t,
      language: i18n.language,
    });

  useEffect(() => {
    if (!isOpen) return;
    emitPaymentDetailsBottomSheetTracking(
      "payment__details_viewed",
      paymentOption,
      ctx.session,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const continueButtonText = t("plan_page_improvements.common.continue");

  const handleContinueClick = async () => {
    try {
      onClose();
      await handleContinueToPayment();
      setTimeout(() => onContinue(), 300);
    } catch (error) {
      console.error("Error continuing to payment:", error);
    }
  };

  return (
    <Modal
      onClose={() => {
        emitModalTracking("order_details_modal_closed", ctx.session, {
          modal_name: "order_details",
        });
        emitPaymentDetailsBottomSheetTracking(
          "payment__payment_details_close_btn_clicked",
          paymentOption,
          ctx.session,
        );
        onClose();
      }}
      stickyFooter={
        <Button size="large" onClick={handleContinueClick}>
          {continueButtonText}
        </Button>
      }
    >
      <div className="pt-6 pr-6 pl-6 border-gray-200 shrink-0">
        <ModalHead onClose={onClose} titleAlignment="left">
          {t("plan.details_title")}
        </ModalHead>
      </div>

      <div className="px-6 space-y-6 overflow-y-auto flex-1 min-h-0 pt-4">
        <div className="space-y-4 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-[#16181D] font-medium">
                {t("order.payment_plan")}
              </span>
              {showProductType &&
                paymentOption.productType &&
                !isUaeBnplPlus && (
                  <div className="flex items-center gap-1.5 text-xs text-[#585B7A]">
                    <span>{t("plan.shariat_compliant")}</span>
                    <span>·</span>
                    <span>
                      {productTypeLabel(paymentOption.productType)}
                    </span>
                  </div>
                )}
            </div>
            <Badge>
              {selectedPayments}{" "}
              {t(paymentsLabelKey, { count: selectedPayments })}
            </Badge>
          </div>

          <ScheduleList
            items={schedulePreview.map(({ dueDate, amount, dueNow }) => [
              dueNow ? ctx.t("order.due_today") : ctx.displayDate(dueDate),
              <MoneyAmount
                key={`${dueDate}-${amount.amount}`}
                amount={amount.amount}
                currency={amount.currency}
                language={i18n.language}
              />,
            ])}
            hideRange={
              schedulePreview.length <= 3
                ? undefined
                : [3, schedulePreview.length - 1]
            }
            expandLabel={t("order.view_all_payments")}
            collapseLabel={t("order.collapse_payments")}
            onExpandClick={() =>
              ctx.sendEvent(events.SCHEDULE_LIST_EXPAND_CLICKED)
            }
            onCollapseClick={() =>
              ctx.sendEvent(events.SCHEDULE_LIST_COLLAPSE_CLICKED)
            }
          />
        </div>

        {/* Order Summary */}
        <Box radius="sm" className="p-4">
          <div className="space-y-3">
            {/* Purchase Amount */}
            <div className="flex justify-between text-sm">
              <span className="text-[#585B7A]">
                {t("order.purchase_amount")}
              </span>
              <MoneyAmount
                amount={ctx.session.orderTotalAmount.amount}
                currency={ctx.session.orderTotalAmount.currency}
                language={i18n.language}
              />
            </div>

            {/* Total Fees */}
            <>
              {" "}
              {murabahaProfit > 0 || processingFeeAmount > 0 ? (
                <>
                  {murabahaProfit > 0 && (
                    <div className="flex justify-between items-center text-sm gap-2 min-w-0">
                      <span className="text-[#585B7A] shrink-0">
                        {t(termCostLineItemLabelKey(isUaeBnplPlus))}
                      </span>
                      <span className="shrink-0">
                        <MoneyAmount
                          amount={murabahaProfit}
                          currency={paymentOption.processingFeeAmount.currency}
                          language={i18n.language}
                        />
                      </span>
                    </div>
                  )}
                  {processingFeeAmount > 0 && (
                    <div className="flex justify-between items-center text-sm gap-2 min-w-0">
                      <span className="text-[#585B7A] shrink-0">
                        {t("total.processing_fee")}
                      </span>
                      <span className="shrink-0">
                        <MoneyAmount
                          amount={processingFeeAmount}
                          currency={paymentOption.processingFeeAmount.currency}
                          language={i18n.language}
                        />
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center text-sm gap-2 min-w-0">
                    <span className="text-[#585B7A] shrink-0">
                      {t("order.total_fees")}
                    </span>
                    {showSubventionFeeStrikethrough ? (
                      <div className="flex items-center gap-2 shrink-0 flex-nowrap">
                        <span className="line-through text-[rgba(126,130,173,1)]">
                          <MoneyAmount
                            amount={strikethroughFeeAmount}
                            currency={
                              paymentOption.totalCustomerFeeAmount.currency
                            }
                            className="line-through"
                            language={i18n.language}
                          />
                        </span>
                        <span className="text-[#128351] font-medium">
                          <MoneyAmount
                            amount={feeAfterWaiverAmount}
                            currency={
                              paymentOption.totalCustomerFeeAmount.currency
                            }
                            language={i18n.language}
                          />
                        </span>
                      </div>
                    ) : totalFees === 0 ? (
                      <span className="text-[#128351] font-medium shrink-0">
                        <MoneyAmount
                          amount={0}
                          currency={
                            paymentOption.totalCustomerFeeAmount.currency
                          }
                          language={i18n.language}
                        />
                      </span>
                    ) : (
                      <span className="shrink-0">
                        <MoneyAmount
                          amount={totalFees}
                          currency={
                            paymentOption.totalCustomerFeeAmount.currency
                          }
                          language={i18n.language}
                        />
                      </span>
                    )}
                  </div>
                </>
              )}
            </>

            {/* Effective APR */}
            {showInlineApr && (
              <div className="flex justify-between text-sm">
                <span className="text-[#585B7A]">
                  {effectiveAprLabel}
                </span>
                <span
                  className={
                    (paymentOption.annualPercentageRate ?? 0) === 0
                      ? "text-[#128351] font-medium"
                      : ""
                  }
                >
                  {(paymentOption.annualPercentageRate ?? 0) === 0
                    ? "0%"
                    : `${aprPercent.toFixed(2)}%`}
                </span>
              </div>
            )}

            {/* Exclusive Discount */}
            {rewardAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-[#585B7A]">
                  {t("cart_reward_exclusive_discount_label")}
                </span>
                <span className="inline-flex items-center gap-1 text-[#128351] font-medium">
                  <RewardsIcon className="w-4 h-4 [&_path]:fill-[#128351]" />
                  <span>-</span>
                  <MoneyAmount
                    amount={rewardAmount}
                    currency={rewardCurrency}
                    className="font-medium"
                    language={i18n.language}
                  />
                </span>
              </div>
            )}

            <div className="border-t border-[#DAE0EB]" />

            <div className="flex justify-between items-center text-base font-medium">
              {showAprDeEmphasis ? (
                <AprDeEmphasisTooltip
                  label={t("order.total_of_payments")}
                  content={t("apr_de_emphasis.total_includes_apr", {
                    rate: aprPercent.toFixed(2),
                  })}
                  onOpen={() =>
                    ctx.sendEvent(events.APR_DE_EMPHASIS_TOOLTIP_CLICKED, {
                      apr: aprPercent.toFixed(2),
                      country_code: countryCode,
                      product_type: paymentOption.productType,
                      tenure: selectedPayments,
                    })
                  }
                />
              ) : (
                <span>{t("order.total_of_payments")}</span>
              )}
              <MoneyAmount
                amount={paymentOption.totalToBeRepaidAmount.amount}
                currency={paymentOption.totalToBeRepaidAmount.currency}
                className="font-medium"
                language={i18n.language}
              />
            </div>
          </div>
        </Box>
      </div>
    </Modal>
  );
}
